import { addToast } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Col, Container, Row } from 'reactstrap';

import GoBackButton from '../../../../../components/buttons/GoBackButton';
import { PathsLayouts } from '../../../../../config/routes';
import { PeriodType } from '../../../../../types/PeriodType';
import { getCurriculumRequest } from '../../api/study-plan-curriculums';
import { DegreeAverageOptions } from '../parts/DegreeAverageOptions';
import { CurriculumDegreeActivitiesList } from '../parts/CurriculumDegreeActivities';
import CurriculumForm from '../CurriculumForm';
import { CurriculumLegend } from '../CurriculumLegend';
import CurriculumStudyPlanInfo from '../CurriculumStudyPlanInfo';
import { DegreeDocuments } from '../parts/DegreeDocuments';

type PropsParams = {
  id: string;
  versionId: string;
  curriculumId: string;
};

const CurriculumView = () => {
  const prefix = 'studyPlans.curriculum';
  const readOnly: boolean = true;

  const history = useHistory();
  const { t } = useTranslation();
  const { id: studyPlanId, versionId, curriculumId } = useParams<PropsParams>();

  const [periodType, setPeriodType] = useState<PeriodType>();
  const [studyPlanName, setStudyPlanName] = useState<string>('');
  const [curriculumData, setCurriculumData] = useState<any>(undefined);
  const [, setDisabledEditButton] = useState<boolean>(false);

  const goBack = useCallback(() => {
    history.push(
      `${PathsLayouts.academicOffers}/study-plans/record/${studyPlanId}`,
    );
  }, [history, studyPlanId]);

  const getCurriculumData = useCallback(
    async (id: string) => {
      const { data, error } = await getCurriculumRequest(id);
      if (data) {
        setCurriculumData(data.data);
      }
      if (error) {
        addToast({
          text: t(`${prefix}.errorOnSearch`),
          color: 'danger',
          icon: 'error',
        });
      }
    },
    [t],
  );

  useEffect(() => {
    if (curriculumId) {
      getCurriculumData(curriculumId);
      setDisabledEditButton(true);
    }
  }, [curriculumId, getCurriculumData]);

  return (
    <Container>
      <Card className="p-3 p-md-4">
        <Row>
          <Col xs={12} md={7}>
            <div className="mb-4">
              <GoBackButton onClick={goBack} />
            </div>
          </Col>
          <Col xs={12} md={5}>
            <CurriculumStudyPlanInfo
              id={studyPlanId}
              versionId={versionId}
              setStudyPlanName={setStudyPlanName}
              setPeriodType={setPeriodType}
            />
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={12}>
            <CurriculumForm
              studyPlanName={studyPlanName}
              curriculumData={curriculumData}
              readOnly={readOnly}
              setDisabledEditButton={setDisabledEditButton}
              isMention={false}
              periodType={periodType}
            >
              {({ legend }) => (
                <>
                  <Row>
                    <Col xs={{ size: 12 }}>
                      {!readOnly && <CurriculumLegend legend={legend} />}
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={{ size: 12 }}>
                      {
                        <DegreeAverageOptions
                          readOnly={readOnly}
                          initialGraduationPercentage={
                            curriculumData?.graduationPercentageForDegreeAverage
                          }
                        />
                      }
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={{ size: 12 }}>
                      {
                        <CurriculumDegreeActivitiesList
                          readOnly={readOnly}
                          curriculumId={curriculumId}
                        />
                      }
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={{ size: 12 }}>
                      <DegreeDocuments
                        degreeDocuments={curriculumData?.degreeDocuments}
                        readOnly={readOnly}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </CurriculumForm>
          </Col>
        </Row>
      </Card>
    </Container>
  );
};

export default CurriculumView;
